import Image from 'next/image'
import classnames from 'classnames'
import { m, LazyMotion, domAnimation, Variants } from 'framer-motion'
import { forwardRef } from 'react'
import { Button } from '@platform/ui-kit'

import RigorousImage from '../../../assets/pngs/rigorous.png'
import QualityImage from '../../../assets/pngs/quality.png'
import ScaleImage from '../../../assets/pngs/scale.png'

import styles from './styles.module.scss'

const CARD_VARIANTS: Variants = {
  start: { y: '50%', opacity: 0 },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const ANIMATION_PROPS = {
  variants: CARD_VARIANTS,
  initial: 'start',
  whileInView: 'end',
  viewport: { once: true },
}

export const TrustNeededSection = forwardRef<HTMLElement>((_, ref) => {
  return (
    <section className={styles['container']} ref={ref}>
      <div className={styles['content']}>
        <LazyMotion features={domAnimation} strict>
          <m.h2 {...ANIMATION_PROPS} className={styles['title']}>
            The trust needed for a successful partnership
          </m.h2>
          <m.p {...ANIMATION_PROPS} className={styles['description']}>
            We work with simple and transparent processes
          </m.p>
          <div className={styles['grid']}>
            <m.div {...ANIMATION_PROPS} className={classnames(styles['card'], styles['rigorous'])}>
              <p className={styles['card-title']}>Ready-to-deliver teams.</p>
              <p className={styles['card-description']}>
                We ensure every talent has the right experience, leadership, and communication
                skills. You manage the projects, and our teams are yours to direct.
              </p>
            </m.div>
            <m.div
              {...ANIMATION_PROPS}
              className={classnames(styles['image-container'], styles['rigorous-image'])}
            >
              <Image
                src={RigorousImage}
                alt="Ready to deliver teams"
                fill
                sizes="(max-width: 703px) 100vw, (max-width: 1279px) 35vw, 36.2rem"
                className={styles['image']}
              />
            </m.div>
            <m.div
              {...ANIMATION_PROPS}
              className={classnames(styles['image-container'], styles['quality-image'])}
            >
              <Image
                src={QualityImage}
                alt="Quality guarantee"
                fill
                sizes="(max-width: 703px) 100vw, (max-width: 1279px) 35vw, 36.2rem"
                className={styles['image']}
              />
            </m.div>
            <m.div {...ANIMATION_PROPS} className={classnames(styles['card'], styles['quality'])}>
              <p className={styles['card-title']}>Quality guarantee.</p>
              <p className={styles['card-description']}>
                Skip the hassle of guessing our talent potential. Instead, onboard quickly and
                experience our immediate impact. If you are not satisfied within two weeks, you pay
                nothing.
              </p>
            </m.div>
            <m.div {...ANIMATION_PROPS} className={classnames(styles['card'], styles['hidden'])}>
              <p className={styles['card-title']}>No hand-holding required.</p>
              <p className={styles['card-description']}>
                Our culture pushes our teams forward personally and professionally, with the energy
                of being one step ahead, meaning you get proactive talent who look to add value from
                day one.
              </p>
            </m.div>
            <m.div {...ANIMATION_PROPS} className={classnames(styles['card'], styles['scale'])}>
              <p className={styles['card-title']}>Scale with ease.</p>
              <p className={styles['card-description']}>
                Our flexible contracts and monthly billing allow you to scale your team on demand or
                ramp down as needed. It's that simple.
              </p>
            </m.div>
            <m.div
              {...ANIMATION_PROPS}
              className={classnames(styles['image-container'], styles['scale-image'])}
            >
              <Image
                src={ScaleImage}
                alt="Rigorous vetting"
                fill
                sizes="(max-width: 703px) 100vw, (max-width: 1279px) 35vw, 50rem"
                className={styles['image']}
              />
            </m.div>
          </div>
          <Button variant="secondary" className={styles['button']} href="/companies" size="large">
            Hire Olby
          </Button>
        </LazyMotion>
      </div>
    </section>
  )
})

export default TrustNeededSection
